import { useEffect, useState } from 'react';

import { useMemberId } from '@trello/authentication';
import { dontUpsell } from '@trello/browser';
import { Entitlements } from '@trello/entitlements';

import { hasAlreadyUsedTrial } from 'app/src/components/FreeTrial/useStartFreeTrialForOrg';
import {
  useEligibleWorkspacesToUpgradeQuery,
  type EligibleWorkspaceToUpgrade,
} from 'app/src/components/SelectWorkspaceToUpgrade';
import { useMemberNodeIdFragment } from './MemberNodeIdFragment.generated';
import { usePlannerWorkspacePremiumFeaturesFragment } from './PlannerWorkspacePremiumFeaturesFragment.generated';

const eligibilityFilter = (workspace: EligibleWorkspaceToUpgrade) =>
  Entitlements.isFree(workspace.offering);

const isEligibleForTrial = (workspace: EligibleWorkspaceToUpgrade) => {
  return !hasAlreadyUsedTrial(workspace.credits ?? []);
};

export type UpsellCohort =
  | 'noUpgradeAvailable'
  | 'paidUser'
  | 'trialAvailable'
  | 'upgradeAvailable'
  | 'upgradeAvailableWithoutUpsell';

export const useUpsellData: () => {
  upsellCohort: UpsellCohort;
  isLoading: boolean;
} = () => {
  const [upsellCohort, setUpsellCohort] = useState<UpsellCohort>('paidUser');
  const [isLoading, setIsLoading] = useState(true);

  const memberId = useMemberId();
  const { data: member } = useMemberNodeIdFragment({
    from: { id: memberId },
    optimistic: true,
  });
  const { data } = usePlannerWorkspacePremiumFeaturesFragment({
    from: { id: member?.nodeId },
    optimistic: true,
  });

  const { workspaces, loading: workspacesLoading } =
    useEligibleWorkspacesToUpgradeQuery({
      filter: eligibilityFilter,
    });

  useEffect(() => {
    if (!workspacesLoading && data?.planner?.workspace) {
      const isEligibleForUpsells =
        !data.planner.workspace.premiumFeatures?.includes('advancedPlanner');

      if (isEligibleForUpsells) {
        if (workspaces.some(isEligibleForTrial)) {
          setUpsellCohort('trialAvailable');
        } else if (!workspaces.length || dontUpsell()) {
          // There are some situations where we want to indicate to the user that they don't have the full
          // Planner experience, but we are not allowed to explicitly show them upsells (e.g. desktop).
          setUpsellCohort('upgradeAvailableWithoutUpsell');
        } else {
          setUpsellCohort('upgradeAvailable');
        }
      } else {
        setUpsellCohort('paidUser');
      }

      setIsLoading(false);
    }
  }, [data?.planner?.workspace, workspaces, workspacesLoading]);

  return { upsellCohort, isLoading };
};
